import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Button, Select, Card, Space, Typography,
  Modal, Input, message, Row, Col, Statistic, Tooltip,
} from 'antd';
import {
  FlagOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, EyeOutlined, CarOutlined, UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../api/config/axiosConfig';

const { Text, Title } = Typography;
const { TextArea } = Input;

const STATUS_COLOR: Record<string, string> = {
  PENDING:   'orange',
  REVIEWED:  'blue',
  DISMISSED: 'default',
  ACTIONED:  'red',
};

const ReportsQueue: React.FC = () => {
  const { t } = useTranslation();

  const REASON_LABEL: Record<string, string> = {
    FAKE_LISTING:      t('admin.reports.reason_fake_listing'),
    WRONG_CATEGORY:    t('admin.reports.reason_wrong_category'),
    OFFENSIVE_CONTENT: t('admin.reports.reason_offensive_content'),
    ALREADY_SOLD:      t('admin.reports.reason_already_sold'),
    WRONG_PRICE:       t('admin.reports.reason_wrong_price'),
    SPAM:              t('admin.reports.reason_spam'),
    OTHER:             t('admin.reports.reason_other'),
  };

  const [reports, setReports]   = useState<any[]>([]);
  const [counts, setCounts]     = useState({ pending: 0, reviewed: 0, dismissed: 0, total: 0 });
  const [loading, setLoading]   = useState(false);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const [resolveModal, setResolveModal] = useState<{ visible: boolean; report: any | null; action: string }>({
    visible: false, report: null, action: '',
  });
  const [adminNotes, setAdminNotes] = useState('');
  const [resolving, setResolving]   = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/admin/reports/counts');
      setCounts(data);
    } catch { /* counts are non-critical */ }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/reports', {
        params: { page, limit: 20, status: statusFilter },
      });
      setReports(data.reports);
      setTotal(data.pagination.total);
    } catch {
      message.error(t('admin.reports.msg_load_failed'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, t]);

  useEffect(() => { fetchReports(); fetchCounts(); }, [fetchReports, fetchCounts]);

  const openResolve = (report: any, action: string) => {
    setResolveModal({ visible: true, report, action });
    setAdminNotes('');
  };

  const handleResolve = async () => {
    if (!resolveModal.report) return;
    setResolving(true);
    try {
      await axiosInstance.patch(`/admin/reports/${resolveModal.report.id}`, {
        status: resolveModal.action,
        adminNotes,
      });
      message.success(t('admin.reports.msg_updated'));
      setResolveModal({ visible: false, report: null, action: '' });
      fetchReports();
      fetchCounts();
    } catch {
      message.error(t('admin.reports.msg_update_failed'));
    } finally {
      setResolving(false);
    }
  };

  const columns = [
    {
      title: t('admin.reports.col_listing'),
      key: 'listing',
      width: 220,
      render: (_: any, r: any) => (
        <Space>
          {r.listingImage
            ? <img src={r.listingImage} alt="" style={{ width: 50, height: 36, objectFit: 'cover', borderRadius: 4 }} />
            : <CarOutlined style={{ fontSize: 24, color: '#ccc' }} />}
          <div>
            <Text strong style={{ fontSize: 12 }}>{r.listingTitle}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>{r.listing?.id?.split('-')[0].toUpperCase()}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: t('admin.reports.col_reporter'),
      key: 'reporter',
      width: 160,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={0}>
          <Space>
            <UserOutlined style={{ color: '#888' }} />
            <Text style={{ fontSize: 12 }}>{r.reporterName}</Text>
          </Space>
          {r.reporter?.email && (
            <Text type="secondary" style={{ fontSize: 11 }}>{r.reporter.email}</Text>
          )}
        </Space>
      ),
    },
    {
      title: t('admin.reports.col_reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 150,
      render: (v: string) => (
        <Tag color="volcano">{REASON_LABEL[v] || v}</Tag>
      ),
    },
    {
      title: t('admin.reports.col_details'),
      dataIndex: 'description',
      key: 'description',
      render: (v: string) => v
        ? <Tooltip title={v}><Text style={{ fontSize: 12 }}>{v.length > 60 ? v.slice(0, 60) + '…' : v}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: t('admin.reports.col_status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('admin.reports.col_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{new Date(v).toLocaleDateString()}</Text>,
    },
    {
      title: t('admin.reports.col_actions'),
      key: 'actions',
      width: 160,
      render: (_: any, r: any) => (
        <Space size={4}>
          {r.status === 'PENDING' && (
            <>
              <Button size="small" icon={<CheckCircleOutlined />} type="primary"
                onClick={() => openResolve(r, 'REVIEWED')} style={{ fontSize: 11 }}>
                {t('admin.reports.btn_review')}
              </Button>
              <Button size="small" icon={<CloseCircleOutlined />} danger
                onClick={() => openResolve(r, 'DISMISSED')} style={{ fontSize: 11 }}>
                {t('admin.reports.btn_dismiss_short')}
              </Button>
            </>
          )}
          {r.listing?.slug && (
            <Tooltip title={t('admin.reports.btn_view_listing')}>
              <Button size="small" icon={<EyeOutlined />}
                onClick={() => window.open(`/listing/${r.listing.slug}`, '_blank')} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <Title level={4} style={{ marginBottom: 20 }}>
        <FlagOutlined style={{ marginRight: 8, color: '#dc2626' }} />
        {t('admin.reports.title')}
      </Title>

      {/* Stat cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { label: t('admin.reports.stat_pending'),   value: counts.pending,   color: '#f97316' },
          { label: t('admin.reports.stat_reviewed'),  value: counts.reviewed,  color: '#3b82f6' },
          { label: t('admin.reports.stat_dismissed'), value: counts.dismissed, color: '#9ca3af' },
          { label: t('admin.reports.stat_total'),     value: counts.total,     color: '#111' },
        ].map((s) => (
          <Col key={s.label} xs={12} sm={6}>
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Statistic title={s.label} value={s.value} valueStyle={{ color: s.color, fontWeight: 700 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        {/* Filters */}
        <Space style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder={t('admin.reports.filter_status')}
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            options={[
              { value: 'PENDING',   label: t('admin.reports.filter_pending') },
              { value: 'REVIEWED',  label: t('admin.reports.filter_reviewed') },
              { value: 'DISMISSED', label: t('admin.reports.filter_dismissed') },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { fetchReports(); fetchCounts(); }}>
            {t('admin.reports.btn_refresh')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={reports}
          rowKey="id"
          loading={loading}
          pagination={{ current: page, total, pageSize: 20, onChange: setPage, showTotal: (count) => t('admin.reports.page_summary', { count }) }}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>

      {/* Resolve modal */}
      <Modal
        open={resolveModal.visible}
        title={resolveModal.action === 'REVIEWED' ? t('admin.reports.btn_mark_reviewed') : t('admin.reports.btn_dismiss')}
        onCancel={() => setResolveModal({ visible: false, report: null, action: '' })}
        footer={null}
        width={440}
      >
        <p style={{ color: 'rgba(0,0,0,0.55)', fontSize: 13 }}>
          {t('admin.reports.modal_listing_label')} <strong>{resolveModal.report?.listingTitle}</strong>
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>{t('admin.reports.modal_notes_label')}</label>
          <TextArea
            rows={3}
            placeholder={t('admin.reports.modal_notes_placeholder')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            style={{ marginTop: 6 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={() => setResolveModal({ visible: false, report: null, action: '' })}>{t('admin.reports.modal_cancel')}</Button>
          <Button
            type="primary"
            danger={resolveModal.action === 'DISMISSED'}
            loading={resolving}
            onClick={handleResolve}
          >
            {t('admin.reports.modal_confirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ReportsQueue;
